import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IThunkAPI } from '@/store/types';
import {
  IVoiceQuestionList,
  IVoiceQuestionState,
  IVoiceQuestionPayload,
  IQuestionCategoryList,
  IVoiceQuestionSavePayload,
} from '../types/types';
import { voiceQuestionApi } from '@/api/admin/voice-interview-question';

const initialState: IVoiceQuestionState = {
  interviewQuestionsList: [],
  allVoiceQuestionsList: [],
  questionCategories: [],
  voiceQuestionCategories: [],
  voiceQuestionUpdated: false,
};

export const getAllVoiceQuestionsList = createAsyncThunk<Array<IVoiceQuestionList>, number, IThunkAPI>(
  'get/voiceQuestion/list/all',
  async (gigId, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.getAllVoiceQuestionsList(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVoiceQuestionCategoriesList = createAsyncThunk<Array<IQuestionCategoryList>, undefined, IThunkAPI>(
  'voice/question/category/list',
  async (_, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.getVoiceQuestionCategoriesList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getVoiceQuestionsList = createAsyncThunk<Array<IVoiceQuestionList>, number, IThunkAPI>(
  'get/voiceQuestion/list',
  async (gigId, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.getVoiceQuestionsList(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const arrangeVoiceQuestionOrder = createAsyncThunk<unknown, IVoiceQuestionPayload, IThunkAPI>(
  'post/voiceQuestion/arrange',
  async (payload, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.arrangeVoiceQuestionOrder(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const saveVoiceQuestion = createAsyncThunk<any, IVoiceQuestionSavePayload, IThunkAPI>(
  'voiceQuestion/save',
  async (payload, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.saveVoiceQuestion(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const assignedVoiceQuestionOrder = createAsyncThunk<unknown, IVoiceQuestionPayload, IThunkAPI>(
  'post/voicewQuestion/assigned',
  async (payload, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.assignedVoiceQuestionOrder(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const deleteVoiceQuestions = createAsyncThunk<Array<IVoiceQuestionList>, number, IThunkAPI>(
  'delete/voiceQuestion/list',
  async (gigId, thunkAPI) => {
    try {
      const response = await voiceQuestionApi.deleteClientVoiceQuestionsList(gigId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const voiceQuestionSlice = createSlice({
  name: 'voiceQuestion',
  initialState,
  reducers: {
    updateAllVoiceQuestions(state, action: PayloadAction<IVoiceQuestionList[]>) {
      state.allVoiceQuestionsList = action.payload;
    },
    updateVoiceQuestions(state, action: PayloadAction<IVoiceQuestionList[]>) {
      state.interviewQuestionsList = action.payload;
    },
    setVoiceQuestionUpdated(state, action: PayloadAction<boolean>) {
      state.voiceQuestionUpdated = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getVoiceQuestionsList.fulfilled, (state, action) => {
      state.interviewQuestionsList = action.payload;
    });
    builder.addCase(getAllVoiceQuestionsList.fulfilled, (state, action) => {
      state.allVoiceQuestionsList = action.payload;
    });
    builder.addCase(getVoiceQuestionCategoriesList.fulfilled, (state, action) => {
      state.voiceQuestionCategories = action.payload;
    });
  },
});

export const { updateAllVoiceQuestions, updateVoiceQuestions, setVoiceQuestionUpdated } = voiceQuestionSlice.actions;

export default voiceQuestionSlice.reducer;
